import { useKeycloak } from '@/shared/_domains/keycloak/modules/useKeycloak';
import { userActualPhone } from '@/shared/_domains/user/functions/userActualPhone';
import { CARD_STATUS_OFF } from '@/shared/constants/card';
import { projectId } from '@/shared/constants/system';
import {
  isCardActive,
  isCardOff,
  isCardPhysical,
  isCardVirtual,
} from '@/shared/lib/core/cards';
import { prop } from '@/shared/lib/core/common';
import { pipe } from '@/shared/lib/core/pipe';
import { useBitrixStore } from '@/shared/store/useBitrixStore';
import axiosGlobal from 'axios';
import { debug } from 'debug';
import { defineStore } from 'pinia';

const operationsMapIds = {
  userRefRegistrations: 'FRIEND',
  userRefPayments: 'ACADEM_ORDER',
};
const userInfoKeysMap: Record<string, string> = {
  'full_name': 'fullName',
};

const localDebug = debug('app:pinia:userStore');
const keycloak = useKeycloak();

export const useUserStore = defineStore('user', {
  state: () => ({
    isCardAutoCreationFailed: undefined,
    isCardAutoCreationProcess: undefined,
    isCardAutoCreated: false,
    isFetched: false,
    userEmail: keycloak.email(),
    balance: 0,
    id: '',
    refRegistrations: [],
    refPayments: [],
    allOperations: [],
    fullName: '',
    partners: [],
    phones: [],
    verifiedPhone: null as string | null,
    verified: {
      phone: null as string | null,
    },
    statusId: '',
    statusName: '',
    paStatusName: '',
    updatedCondition: 0,
    imgUrl: '',
    physicalUserCard: {} as any,
    virtualUserCard: {},
    userNameFromKC: null as {firstName: string, lastName: string} | null,
    deals: [] as any[],
    isDealsFetched: false,
    manager: null,
    bitrixId: 0,
  }),
  actions: {
    async fetchUserDeals() {
      const { data } = await axiosGlobal.get('v2/api/contacts', {
        params: {
          email: this.userEmail,
          include: 'deals,hasWin',
        },
      });
      this.deals = data.response.contacts[0]?.deals;
      this.isDealsFetched = true;
    },
    async fetchUserInfo() {
      const { data } = await axiosGlobal.get('v2/api/contacts', {
        params: {
          email: this.userEmail,
          include: 'manager,hasWin',
        },
      });
      this.fillUserInfo(this, data.response.contacts[0]);
      this.isFetched = true;
      await this.fetchAllOperations();
    },
    fillUserInfo(state: any, contact: any) {
      if (!contact) {
        localDebug('no user info');
        return;
      }

      localDebug('user info is', contact);
      for (const key in contact) {
        const actualKey = userInfoKeysMap[key] ?? key;
        if (state[actualKey] !== undefined) {
          state[actualKey] = contact[key];
        }
      }
    },
    async fetchPhysicalUserCard() {
      const res = await axiosGlobal
        .get('api/cards', {
          params: {
            email: this.userEmail,
          },
        });
      const { data } = res;
      if (data.response?.cards.length) {
        const { cards } = data.response;
        const physicalCards = cards.filter(
          pipe(prop('typeId', 0)).pipeToFunc(isCardPhysical));
        const virtualCard = cards.find(
          pipe(prop('typeId', 0)).pipeToFunc(isCardVirtual));

        if (virtualCard) {
          this.virtualUserCard = virtualCard;
        }

        const offPhysicalCard = physicalCards.find(
          pipe(prop('status', 0)).pipeToFunc(isCardOff));
        const activePhysicalCards = physicalCards.filter(
          pipe(prop('status', 0)).pipeToFunc(isCardActive));

        if (offPhysicalCard) {
          localDebug('Найдены отключенные');
          this.physicalUserCard = offPhysicalCard;
        } else if (!activePhysicalCards.length && physicalCards.length) {
          localDebug('Нет активных но есть физические');
          physicalCards[0].status = CARD_STATUS_OFF;
          this.physicalUserCard = physicalCards[0];
        } else if (activePhysicalCards.length > 1) {
          localDebug('Активных физических больше 1');
          activePhysicalCards[0].status = CARD_STATUS_OFF;
          this.physicalUserCard = activePhysicalCards[0];
        } else if (activePhysicalCards.length === 1) {
          localDebug('Одна активная');
          this.physicalUserCard = activePhysicalCards[0];
        } else {
          localDebug('Нет карт');
        }
      }
    },
    async fetchRefRegistrations() {
      const res = await axiosGlobal
        .get('api/contacts/detail', {
          params: {
            email: this.userEmail,
            xmlId: operationsMapIds.userRefRegistrations,
          },
        });
      const { data } = res;
      this.refRegistrations = data.response.data;
    },
    async fetchRefPayments() {
      const res = await axiosGlobal
        .get('api/contacts/detail', {
          params: {
            email: this.userEmail,
            xmlId: operationsMapIds.userRefPayments,
          },
        });
      const { data } = res;
      this.refPayments = data.response.data;
    },
    async fetchAllOperations() {
      const res = await axiosGlobal
        .get('v2/api/operations', {
          params: { email: this.userEmail },
        });
      const { data } = res;
      this.allOperations = data.response.operations;
    },
    setUserEmailFromKC(email: string) {
      this.userEmail = email;
    },
    setUserNameFromKC({ firstName, lastName }: any) {
      this.userNameFromKC = {
        firstName,
        lastName,
      };
    },
    setUserBalance(payload: number) {
      this.balance = payload;
    },
    setUserInfoProperty([property, value]: [string, string]) {
      (this as any)[property] = value;
    },
    setIsCardAutoCreated(payload: any) {
      this.isCardAutoCreated = payload;
    },
    setIsCardAutoCreationProcess(payload: any) {
      this.isCardAutoCreationProcess = payload;
    },
    setIsCardAutoCreationFailed(payload: any) {
      this.isCardAutoCreationFailed = payload;
    },
    setPhysicalUserCard(physicalUserCard: any) {
      if (physicalUserCard) {
        this.physicalUserCard = physicalUserCard;
      }
    },
  },
  getters: {
    isStudent(state) {
      return state.deals.some(d => d.stageId === 'WON');
    },
    userDeals(state) {
      if (!state.deals?.length) {
        return [];
      }

      return state.deals
        .filter(({ docNum, partnerId }) => !!docNum && partnerId === projectId)
        .map(({ docNum, id }) => ({ id, docNum, label: docNum }));
    },
    isUserDealsFetched(state) {
      return state.isDealsFetched;
    },
    userBalance: state => state.balance,
    getUserInfo: state => state,
    hasActivePhysicalUserCard: state => {
      return state.physicalUserCard?.status === 1;
    },
    physicalUserCardId: state => {
      return state.physicalUserCard?.id ?? null;
    },
    actualPhone(state) {
      const bitrixStore = useBitrixStore();
      const b24VerifiedPhone = state.verified.phone;
      const bitrixPhone = bitrixStore.client.phone ?? '';
      const firstSVCPhone = state.phones[0];
      return userActualPhone(String(b24VerifiedPhone), String(bitrixPhone), firstSVCPhone);
    },
    partnerId() {
      return 1;
    },
  },
});
